import {parseISO} from "date-fns";

import {Component} from "@/lib/types/statuspages.ts";

export type ComponentSortOptions = "default" | "status_most_severe" | "alphabetical_asc" | "most_recently_updated";

/**
 * Newest incident activity on a component, as epoch ms. A group has no activity of its own, so it
 * takes the newest of its subcomponents — the same roll-up `status_most_severe` applies to severity.
 * Returns 0 when no incident has ever affected the component, sorting it below everything that has.
 */
const getLastUpdatedTime = (component: Component): number => {
  const own = component.last_updated_at ? parseISO(component.last_updated_at).getTime() : 0;
  let latest = Number.isNaN(own) ? 0 : own;

  component.subcomponents?.forEach((sub) => {
    latest = Math.max(latest, getLastUpdatedTime(sub));
  });

  return latest;
};

export const COMPONENT_SORT_OPTIONS: {value: ComponentSortOptions; label: string}[] = [
  {value: "default", label: "Default"},
  {value: "status_most_severe", label: "Status (Most Severe)"},
  {value: "alphabetical_asc", label: "Alphabetical (A-Z)"},
  {value: "most_recently_updated", label: "Most Recently Updated"},
];

export const sortComponents = (
  components: Component[],
  sortBy: ComponentSortOptions,
  componentStatusRank: Record<string, number>
): Component[] => {
  const sorted = [...components];

  switch (sortBy) {
    case "default":
      // Dashboard order: the backend orders components by `sorting_weight, id` (the weights the
      // drag-and-drop in the components tab writes), so leaving the payload order alone reproduces
      // what the legacy status page renders.
      break;

    case "status_most_severe":
      // Sort by status severity (lower rank = more severe, based on backend enum)
      // MAJOR_OUTAGE: rank 1 (most severe)
      // PARTIAL_OUTAGE: rank 2
      // DEGRADED_PERFORMANCE: rank 3
      // NOTIFICATION: rank 4
      // UNDER_MAINTENANCE: rank 5
      // OPERATIONAL: rank 6 (least severe)
      sorted.sort((a, b) => {
        const getWorstRank = (component: Component): number => {
          let worstRank = componentStatusRank[component.status] || 999;

          // Check subcomponents for worse status (lower rank number)
          if (component.subcomponents && component.subcomponents.length > 0) {
            component.subcomponents.forEach((sub) => {
              const subRank = getWorstRank(sub);
              if (subRank < worstRank) {
                worstRank = subRank;
              }
            });
          }

          return worstRank;
        };

        const rankA = getWorstRank(a);
        const rankB = getWorstRank(b);

        // Lower rank (more severe) should come first
        if (rankA !== rankB) {
          return rankA - rankB;
        }

        // If same severity, sort alphabetically
        return a.name.localeCompare(b.name);
      });
      break;

    case "alphabetical_asc":
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      break;

    case "most_recently_updated":
      // Most recent incident activity first, then components no incident has ever touched,
      // alphabetically among ties.
      sorted.sort((a, b) => {
        const timeA = getLastUpdatedTime(a);
        const timeB = getLastUpdatedTime(b);

        if (timeA !== timeB) {
          return timeB - timeA;
        }

        return a.name.localeCompare(b.name);
      });
      break;

    default:
      break;
  }

  return sorted;
};
